import { CardHeader as ContentCardHeader } from '@hue/design-system';
import { ClipboardList as ContentCardIcon } from 'lucide-react';
import { answerText, compareRecordVersions } from '@hue/contracts';
import { Button, ChoiceChips, StatusPill } from '@hue/design-system';
import { ArrowLeft, GitCompareArrows, PencilLine } from 'lucide-react';
import type { ReactNode } from 'react';
import type { ClientChart, ClientRecord } from '../../lib/api.js';
import { formatDate, formatDateTime } from '../../lib/time-zone.js';
import { Notice, RichText, ThemeTag } from './shared.js';
import { PrintButton, SignaturePill, SignatureSection } from './signature.js';
import { blocksOf, documentsOf, themeOf } from './helpers.js';

function versionOption(
  version: ClientRecord['versions'][number],
  timeZone: string,
  chart: ClientChart,
) {
  const signed = documentsOf(chart.documents, version.id).some(
    (document) => document.kind === 'RECORD',
  );
  return {
    value: String(version.version),
    label: `v${String(version.version)} · ${formatDate(version.recordedAt, timeZone)}${signed ? ' · assinada' : ''}`,
  };
}

function templateVersionNumber(chart: ClientChart, templateVersionId: string): number {
  return chart.templateVersions.find((version) => version.id === templateVersionId)!.version;
}

/// Uma versão da ficha, só leitura, com as perguntas da versão do modelo que ela respondeu. Daqui se
/// compara qualquer versão com qualquer outra (ADR 0038, decisão 6).
export function RecordView({
  chart,
  record,
  version,
  compare,
  timeZone,
  canManage,
  onSelectVersion,
  onCompare,
  onEdit,
  onBack,
  onSigned,
  consents,
}: {
  chart: ClientChart;
  record: ClientRecord;
  /// A versão exibida; nula mostra a vigente.
  version: number | null;
  /// Com qual versão comparar; nula mostra as respostas.
  compare: number | null;
  timeZone: string;
  canManage: boolean;
  onSelectVersion: (version: number) => void;
  onCompare: (version: number | null) => void;
  onEdit: () => void;
  onBack: () => void;
  /// Depois de assinar ou anexar a via: o prontuário é lido de novo.
  onSigned: () => Promise<void>;
  /// As autorizações do cadastro, montadas pela composição (ADR 0040, decisão 6).
  consents?: ReactNode;
}) {
  const template = chart.templates.find((item) => item.id === record.templateId)!;
  const current = record.versions.at(-1)!;
  const shown = record.versions.find((item) => item.version === version) ?? current;
  const isCurrent = shown === current;
  const blocks = blocksOf(chart, shown.templateVersionId);
  const answeredTemplate = templateVersionNumber(chart, shown.templateVersionId);
  const other = record.versions.find((item) => item.version === compare && item !== shown);
  const defaultOther = record.versions.find((item) => item !== shown);

  const documents = documentsOf(chart.documents, shown.id);
  const ownDocument = documents.some((document) => document.kind === 'RECORD');

  return (
    <div className="grid gap-4">
      <div>
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="size-4" />
          Voltar ao prontuário
        </Button>
      </div>
      <div className="grid gap-4 rounded-2xl border border-surface-border bg-surface-card p-5 shadow-soft">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="grid gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <ContentCardHeader
                title={`${template.displayName} · versão ${shown.version}`}
                icon={<ContentCardIcon className="size-4" />}
              />
              {isCurrent ? (
                <StatusPill tone="positive">Vigente</StatusPill>
              ) : (
                <StatusPill tone="neutral">Histórico</StatusPill>
              )}
              <SignaturePill template={template} versionId={shown.id} documents={chart.documents} />
            </div>
            <p className="text-xs text-content-muted">
              Gravada por {shown.recordedByDisplayName} em{' '}
              {formatDateTime(shown.recordedAt, timeZone)} · modelo v{answeredTemplate}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {isCurrent ? (
              <PrintButton
                scope={{ kind: 'RECORD', versionId: shown.id }}
                fileParts={['ficha', template.displayName, chart.client.displayName]}
              />
            ) : null}
            {defaultOther ? (
              <Button
                variant="outline"
                size="sm"
                aria-pressed={Boolean(other)}
                onClick={() => onCompare(other ? null : defaultOther.version)}
              >
                <GitCompareArrows className="size-4" />
                {other ? 'Ver respostas' : 'Comparar versões'}
              </Button>
            ) : null}
            {isCurrent && canManage ? (
              <Button size="sm" onClick={onEdit}>
                <PencilLine className="size-4" />
                Editar ficha
              </Button>
            ) : null}
          </div>
        </div>

        {isCurrent ? null : (
          <Notice tone="info">
            Versão antiga, só leitura. Mostra as perguntas como eram no modelo v{answeredTemplate}.
          </Notice>
        )}
        {isCurrent && answeredTemplate < template.latestVersion ? (
          <Notice tone="warning">
            O modelo mudou (v{template.latestVersion}). Ao editar, a ficha passa para o modelo novo
            e as respostas das perguntas que continuam vêm preenchidas.
          </Notice>
        ) : null}

        {other ? (
          <Comparison
            chart={chart}
            record={record}
            shownVersion={shown.version}
            otherVersion={other.version}
            timeZone={timeZone}
            onCompare={onCompare}
          />
        ) : (
          <dl className="divide-y divide-surface-border rounded-xl border border-surface-border">
            {blocks.map((block) => {
              if (block.kind === 'SUBHEADING') {
                return (
                  <div key={block.id} className="bg-content/5 px-4 py-2">
                    <dt className="text-xs font-semibold uppercase tracking-wide text-content-faint">
                      {block.text}
                    </dt>
                  </div>
                );
              }
              if (block.kind !== 'QUESTION') return null;
              const answer = shown.answers[block.id];
              const text = answerText(block, answer);
              return (
                <div
                  key={block.id}
                  className="grid gap-1 px-4 py-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] sm:gap-4"
                >
                  <dt className="flex flex-wrap items-center gap-2 text-sm text-content-muted">
                    {block.label}
                    {block.themeKey ? (
                      <ThemeTag theme={themeOf(chart.themes, block.themeKey)} />
                    ) : null}
                  </dt>
                  <dd className="text-sm">
                    {text === null ? (
                      <span className="text-content-faint">Não respondida</span>
                    ) : block.type === 'LONG_TEXT' ? (
                      <RichText html={answer as string} />
                    ) : (
                      text
                    )}
                  </dd>
                </div>
              );
            })}
          </dl>
        )}

        {other ? null : (
          <>
            {isCurrent ? consents : null}
            <SignatureSection
              key={shown.id}
              scope={{ kind: 'RECORD', versionId: shown.id }}
              heading="Assinatura"
              documents={documents}
              pendingText={
                ownDocument
                  ? null
                  : template.signaturePolicy === 'EVERY_VERSION'
                    ? 'Pendente: este modelo pede assinatura em toda versão.'
                    : 'Esta versão não foi assinada.'
              }
              pending={template.signaturePolicy === 'EVERY_VERSION'}
              signable={{ screen: isCurrent && !ownDocument, paper: !ownDocument }}
              clientName={chart.client.displayName}
              title={`Ficha ${template.displayName}`}
              timeZone={timeZone}
              canManage={canManage}
              consents={consents}
              onSigned={onSigned}
            />
          </>
        )}

        <div className="grid gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-content-faint">
            Versões
          </h3>
          <ChoiceChips
            label="Versões da ficha"
            placeholder="Buscar versão"
            value={String(shown.version)}
            options={[...record.versions]
              .reverse()
              .map((item) => versionOption(item, timeZone, chart))}
            onChange={(value) => onSelectVersion(Number(value))}
          />
        </div>
      </div>
    </div>
  );
}

function Comparison({
  chart,
  record,
  shownVersion,
  otherVersion,
  timeZone,
  onCompare,
}: {
  chart: ClientChart;
  record: ClientRecord;
  shownVersion: number;
  otherVersion: number;
  timeZone: string;
  onCompare: (version: number) => void;
}) {
  const [older, newer] = [shownVersion, otherVersion]
    .sort((left, right) => left - right)
    .map((number) => record.versions.find((item) => item.version === number)!) as [
    ClientRecord['versions'][number],
    ClientRecord['versions'][number],
  ];
  const changes = compareRecordVersions(
    { blocks: blocksOf(chart, older.templateVersionId), answers: older.answers },
    { blocks: blocksOf(chart, newer.templateVersionId), answers: newer.answers },
  );
  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-content-muted">Comparar a versão {shownVersion} com:</span>
        <ChoiceChips
          label="Versão para comparar"
          placeholder="Buscar versão"
          className="w-auto min-w-56 flex-1"
          value={String(otherVersion)}
          options={record.versions
            .filter((item) => item.version !== shownVersion)
            .reverse()
            .map((item) => versionOption(item, timeZone, chart))}
          onChange={(value) => onCompare(Number(value))}
        />
      </div>
      <h3 className="text-sm font-semibold">
        O que mudou da versão {older.version} ({formatDate(older.recordedAt, timeZone)}) para a{' '}
        {newer.version} ({formatDate(newer.recordedAt, timeZone)})
      </h3>
      {changes.length ? (
        <div className="overflow-x-auto rounded-xl border border-surface-border">
          <table className="w-full min-w-[32rem] border-collapse text-left text-sm">
            <caption className="sr-only">Respostas que mudaram entre as versões</caption>
            <thead>
              <tr className="border-b border-surface-border text-xs text-content-muted">
                <th className="px-4 py-2 font-semibold">Pergunta</th>
                <th className="px-4 py-2 font-semibold">Antes</th>
                <th className="px-4 py-2 font-semibold">Depois</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {changes.map((change) => (
                <tr key={change.questionId}>
                  <td className="px-4 py-2 text-content-muted">{change.label}</td>
                  <td className="px-4 py-2 text-danger line-through decoration-danger/50">
                    {change.outsideTemplate === 'BEFORE'
                      ? '(pergunta não existia)'
                      : (change.before ?? '—')}
                  </td>
                  <td className="px-4 py-2 font-semibold text-positive">
                    {change.outsideTemplate === 'AFTER'
                      ? '(pergunta saiu do modelo)'
                      : (change.after ?? '—')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-content-faint">Nenhuma resposta mudou.</p>
      )}
    </div>
  );
}
